/** @format */

import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useStripe } from "@stripe/react-stripe-js";
import { Box, Grid, Typography, CircularProgress } from "@mui/material";
import {
    CheckCircle,
    AutoAwesome,
    ReplayCircleFilled,
} from "@mui/icons-material";

import { AppDispatch } from "../../../redux/store/store";
import {
    selectActiveBusiness,
    selectActiveBusinessId,
    selectActiveBusinessRole,
} from "../../../redux/selectors/collectSelector";
import {
    selectCollectSubscription,
    selectCollectSubscriptionLoading,
    selectCollectBillingSubmitting,
} from "../../../redux/selectors/collectBillingSelector";
import {
    fetchCollectSubscription,
    createCollectSubscription,
    resubscribeCollectSubscription,
} from "../../../redux/actions/collectBillingAction";
import { colors } from "../../../utility/types/constant";
import { useToast } from "../../../providers/ToastProvider";
import {
    CollectPageWrapper,
    CollectHeroBanner,
    CollectHeroInner,
    CollectBadge,
    CollectHeroTitle,
    CollectHeroSubtitle,
    CollectContentArea,
    CollectSectionCard,
    CollectSectionTitle,
    CollectSectionSubtitle,
    CollectPrimaryButton,
    CollectInfoBanner,
} from "../styles";
import CollectPaymentForm from "./CollectPaymentForm";
import {
    COLLECT_INTRO_CYCLES,
    collectIntroPrice,
    collectStandardPrice,
    formatCollectDate,
    daysUntil,
} from "./collectPlanPricing";

// ─────────────────────────────────────────────────────────────────────────
// CollectSubscribe — pricing + checkout screen (Story 2).
//
// Renders one of three states depending on the current subscription:
//   • Already active → gentle redirect to the billing page.
//   • Cancelled + retained + inside the reactivation window → "Welcome back"
//     one-click resubscribe that reuses the retained card (no re-entry).
//   • New / purged / expired → full pricing + card checkout.
// ─────────────────────────────────────────────────────────────────────────

const PLAN_BENEFITS = [
    "Automated payment reminders across email & SMS",
    "Live A/R aging and collections analytics",
    "AI-drafted recovery messages & promise tracking",
    "QuickBooks sync, tasks, and team collaboration",
];

const CollectSubscribe: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const toast = useToast();
    const stripe = useStripe();

    const activeBusiness = useSelector(selectActiveBusiness);
    const activeBusinessId = useSelector(selectActiveBusinessId);
    const role = useSelector(selectActiveBusinessRole);
    const subscription = useSelector(selectCollectSubscription);
    const isLoading = useSelector(selectCollectSubscriptionLoading);
    const isSubmitting = useSelector(selectCollectBillingSubmitting);

    const isOwner = role === "owner";

    const currency = useMemo(
        () =>
            subscription?.currency ||
            activeBusiness?.currency ||
            "usd",
        [subscription?.currency, activeBusiness?.currency],
    );

    // Load current subscription for this tenant.
    useEffect(() => {
        if (activeBusinessId) {
            dispatch(fetchCollectSubscription(undefined));
        }
    }, [dispatch, activeBusinessId]);

    // Already has a paid/live subscription → send them to billing. A trialing
    // record is NOT a paid subscription (no card on file yet), so trial users
    // stay here to enter payment and convert their trial.
    const hasActiveSubscription =
        subscription != null &&
        ["active", "pending", "past_due", "pending_cancellation"].includes(
            subscription.status,
        );

    useEffect(() => {
        if (hasActiveSubscription) {
            navigate("/collect/billing", { replace: true });
        }
    }, [hasActiveSubscription, navigate]);

    // "Welcome back" window: cancelled, card retained, still inside the
    // reactivation window → one-click resubscribe without re-entering a card.
    const withinReactivationWindow =
        subscription != null &&
        subscription.status === "cancelled" &&
        subscription.retentionState === "retained" &&
        !!subscription.reactivationWindowEndsAt &&
        daysUntil(subscription.reactivationWindowEndsAt) > 0;

    const daysLeft = withinReactivationWindow
        ? daysUntil(subscription?.reactivationWindowEndsAt)
        : 0;

    const handleOneClickResubscribe = async () => {
        try {
            const result = await dispatch(
                resubscribeCollectSubscription({}),
            ).unwrap();
            // A retained card can still trigger a 3-D Secure challenge — finish
            // it client-side before declaring success.
            if (result.requiresAction && result.clientSecret) {
                if (!stripe) {
                    throw new Error(
                        "Payment could not be confirmed. Please try again.",
                    );
                }
                const { error: confirmError } =
                    await stripe.confirmCardPayment(result.clientSecret);
                if (confirmError) {
                    throw new Error(
                        confirmError.message ||
                        "We couldn't confirm that payment. Please try a different card.",
                    );
                }
            }
            dispatch(fetchCollectSubscription(undefined));
            toast.success("Welcome back! Your Collect subscription is active.");
            navigate("/collect", { replace: true });
        } catch (e: any) {
            toast.error(e?.message || "We couldn't resubscribe. Please try again.");
        }
    };

    const handleCreateWithCard = async (paymentMethodId: string) => {
        // Reuse resubscribe when a purged/expired subscription record exists,
        // otherwise create fresh. Both return the same SCA hint shape.
        const isResubscribe = subscription != null;
        const action = isResubscribe
            ? resubscribeCollectSubscription({ paymentMethodId })
            : createCollectSubscription({ paymentMethodId });
        return await dispatch(action).unwrap();
    };

    const handleCheckoutSuccess = () => {
        toast.success("You're subscribed! Welcome to Collect.");
        dispatch(fetchCollectSubscription(undefined));
        navigate("/collect", { replace: true });
    };

    if (isLoading && !subscription) {
        return (
            <CollectPageWrapper>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: "50vh",
                    }}
                >
                    <CircularProgress sx={{ color: colors.mainSecondary }} />
                </Box>
            </CollectPageWrapper>
        );
    }

    return (
        <CollectPageWrapper>
            <CollectHeroBanner>
                <CollectHeroInner>
                    <CollectBadge label="Collect" />
                    <CollectHeroTitle>
                        Get paid faster, on autopilot
                    </CollectHeroTitle>
                    <CollectHeroSubtitle>
                        Start recovering overdue invoices today. Cancel anytime.
                    </CollectHeroSubtitle>
                </CollectHeroInner>
            </CollectHeroBanner>

            <CollectContentArea>
                {!isOwner && (
                    <CollectInfoBanner tone="warn">
                        Only the business owner can manage the Collect
                        subscription. Please ask your owner to subscribe.
                    </CollectInfoBanner>
                )}

                <Grid container spacing={3}>
                    {/* ── Plan summary ─────────────────────────────────── */}
                    <Grid item xs={12} md={6}>
                        <CollectSectionCard>
                            <CollectSectionTitle>
                                Collect Monthly plan
                            </CollectSectionTitle>
                            <CollectSectionSubtitle>
                                Everything you need to automate collections.
                            </CollectSectionSubtitle>

                            <Box sx={{ my: 2 }}>
                                <Typography
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontSize: "2.4rem",
                                        fontWeight: 800,
                                        color: colors.primary,
                                        lineHeight: 1.1,
                                    }}
                                >
                                    {collectIntroPrice(currency)}
                                    <Typography
                                        component="span"
                                        sx={{
                                            fontSize: "1rem",
                                            fontWeight: 600,
                                            color: "#6b7280",
                                            ml: 0.5,
                                        }}
                                    >
                                        /month
                                    </Typography>
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontSize: "0.9rem",
                                        color: "#6b7280",
                                        mt: 0.5,
                                    }}
                                >
                                    for your first {COLLECT_INTRO_CYCLES} months,
                                    then {collectStandardPrice(currency)}/month
                                    ongoing.
                                </Typography>
                            </Box>

                            <Box sx={{ mt: 2 }}>
                                {PLAN_BENEFITS.map((benefit) => (
                                    <Box
                                        key={benefit}
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 1,
                                            mb: 1.2,
                                        }}
                                    >
                                        <CheckCircle
                                            sx={{
                                                fontSize: 18,
                                                color: colors.mainSecondary,
                                                mt: "1px",
                                            }}
                                        />
                                        <Typography
                                            sx={{
                                                fontFamily:
                                                    "'Montserrat', sans-serif",
                                                fontSize: "0.9rem",
                                                color: "#374151",
                                            }}
                                        >
                                            {benefit}
                                        </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </CollectSectionCard>
                    </Grid>

                    {/* ── Checkout / resubscribe ───────────────────────── */}
                    <Grid item xs={12} md={6}>
                        <CollectSectionCard>
                            {withinReactivationWindow ? (
                                <>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                            mb: 1,
                                        }}
                                    >
                                        <ReplayCircleFilled
                                            sx={{
                                                color: colors.mainSecondary,
                                            }}
                                        />
                                        <CollectSectionTitle>
                                            Welcome back
                                        </CollectSectionTitle>
                                    </Box>
                                    <CollectSectionSubtitle>
                                        Your account is still ready and your saved
                                        card is on file. Resubscribe in one click —
                                        no need to re-enter anything.
                                    </CollectSectionSubtitle>

                                    <CollectInfoBanner tone="ok">
                                        {daysLeft} day{daysLeft === 1 ? "" : "s"}{" "}
                                        left to restore your account instantly.
                                    </CollectInfoBanner>

                                    {isOwner ? (
                                        <CollectPrimaryButton
                                            fullWidth
                                            onClick={handleOneClickResubscribe}
                                            disabled={isSubmitting}
                                            startIcon={
                                                isSubmitting ? (
                                                    <CircularProgress
                                                        size={18}
                                                        sx={{ color: "#fff" }}
                                                    />
                                                ) : (
                                                    <AutoAwesome
                                                        sx={{ fontSize: 18 }}
                                                    />
                                                )
                                            }
                                        >
                                            {isSubmitting
                                                ? "Restoring…"
                                                : "Resubscribe in one click"}
                                        </CollectPrimaryButton>
                                    ) : (
                                        <Typography
                                            sx={{
                                                fontFamily:
                                                    "'Montserrat', sans-serif",
                                                fontSize: "0.875rem",
                                                color: "#6b7280",
                                            }}
                                        >
                                            Ask your business owner to resubscribe.
                                        </Typography>
                                    )}
                                </>
                            ) : (
                                <>
                                    <CollectSectionTitle>
                                        Payment details
                                    </CollectSectionTitle>
                                    <CollectSectionSubtitle>
                                        You'll be charged{" "}
                                        {collectIntroPrice(currency)} today, then{" "}
                                        {collectIntroPrice(currency)}/month for{" "}
                                        {COLLECT_INTRO_CYCLES} months.
                                        {subscription?.introEndsAt
                                            ? ` Standard pricing of ${collectStandardPrice(
                                                currency,
                                            )}/month starts ${formatCollectDate(
                                                subscription.introEndsAt,
                                            )}.`
                                            : ` After that, ${collectStandardPrice(
                                                currency,
                                            )}/month.`}
                                    </CollectSectionSubtitle>

                                    <Box sx={{ mt: 2 }}>
                                        {isOwner ? (
                                            <CollectPaymentForm
                                                submitLabel={`Subscribe ${collectIntroPrice(
                                                    currency,
                                                )}/month`}
                                                onCreatePaymentMethod={
                                                    handleCreateWithCard
                                                }
                                                onSuccess={handleCheckoutSuccess}
                                                defaultCardholderName={
                                                    activeBusiness?.name
                                                }
                                                busy={isSubmitting}
                                            />
                                        ) : (
                                            <Typography
                                                sx={{
                                                    fontFamily:
                                                        "'Montserrat', sans-serif",
                                                    fontSize: "0.875rem",
                                                    color: "#6b7280",
                                                }}
                                            >
                                                Ask your business owner to add a
                                                payment method and subscribe.
                                            </Typography>
                                        )}
                                    </Box>
                                </>
                            )}
                        </CollectSectionCard>
                    </Grid>
                </Grid>
            </CollectContentArea>
        </CollectPageWrapper>
    );
};

export default CollectSubscribe;
